import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FileFilterModel,
  useFileFilter,
  useSetFileFilter,
} from '@gripp/shared-logic';
import {
  BaseColumnCheckbox,
  Colors,
  getCategoryColumn,
} from '@gripp/shared-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';

export const useColumnsFile = (
  showCheckboxColumn: boolean,
  countButton: (data: any) => React.JSX.Element,
  isProducerWorkspace = false,
  onFileNameClick?: (file: any) => void
) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper();
  const { mutateAsync: setFileFilter } = useSetFileFilter();
  const { data: fileFilter } = useFileFilter();

  const [columns, setColumns] = useState<ColumnDef<unknown, never>[]>([]);
  const updateCategoryFilter = async (
    categoryId: string,
    fileFilter: FileFilterModel,
    disabled = false
  ) => {
    if (!disabled)
      await setFileFilter({
        ...fileFilter,
        categories: [categoryId],
      });
  };
  const allColumns: Record<string, ColumnDef<unknown, never>> = useMemo(
    () => ({
      selected: columnHelper.accessor('selected', {
        cell: BaseColumnCheckbox.checkbox,
        header: BaseColumnCheckbox.checkboxHeader,
        meta: {
          style: styles.checkboxCell,
          exportable: false,
        },
      }),
      name: columnHelper.accessor('name', {
        cell: (info) => {
          const name = info.getValue();
          const row = info.row.original;
          return (
            <Pressable
              onPress={() => onFileNameClick?.(row)}
              style={styles.nameCell}
            >
              <Text
                style={[styles.cellText, styles.clickableFileName]}
                numberOfLines={1}
              >
                {name}
              </Text>
            </Pressable>
          );
        },
        header: () => t('file.list.name'),
        meta: {
          sortable: true,
          exportable: true,
          style: styles.cell,
        },
      }),
      productsCount: columnHelper.accessor('productsCount', {
        cell: (info) => {
          const productsCount = info.getValue();
          const row = info.row.original as any;
          return countButton({
            iconName: 'tag',
            count: productsCount,
            fileId: row['id'],
            name: row['name'],
            countType: 'product',
          });
        },
        header: () => t('file.filter.columns.productsCount'),
        meta: {
          sortable: true,
          exportable: true,
        },
      }),
      assetsCount: columnHelper.accessor('assetsCount', {
        cell: (info) => {
          const assetsCount = info.getValue();
          return (
            <View style={styles.countContainer}>
              <MaterialCommunityIcons
                name="qrcode"
                size={20}
                style={styles.countIcon}
              />
              <Text style={styles.cellText}>{assetsCount}</Text>
            </View>
          );
        },
        header: () => t('file.filter.columns.assetsCount'),
        meta: {
          sortable: true,
          exportable: true,
        },
      }),
      addonsCount: columnHelper.accessor('addonsCount', {
        cell: (info) => {
          const addonsCount = info.getValue();
          const row = info.row.original as any;
          return countButton({
            iconName: 'shape-outline',
            count: addonsCount,
            fileId: row['id'],
            name: row['name'],
            countType: 'addon',
          });
        },
        header: () => t('file.filter.columns.addonsCount'),
        meta: {
          sortable: true,
          exportable: true,
        },
      }),
      categories: columnHelper.accessor('categories', {
        cell: (info) =>
          getCategoryColumn(
            info,
            async (groupId) =>
              await updateCategoryFilter(groupId, fileFilter, true)
          ),
        header: () => t('file.filter.columns.categories'),
        meta: {
          sortable: true,
          exportable: true,
          exportCell: (data: any) =>
            data.categories?.map((category: any) => category.name).join(', ') ||
            '',
        },
      }),
      updatedAt: columnHelper.accessor('updatedAt', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text style={styles.cellText}>{date.fromNow()}</Text>;
          }
          return <Text style={styles.cellText}>-</Text>;
        },
        header: () => t('file.filter.columns.updatedAt'),
        meta: {
          sortable: true,
          exportable: true,
          exportCell: (data: any) => {
            if (data.updatedAt) {
              return moment(data.updatedAt).format('YYYY-MM-DD HH:mm:ss');
            }
            return '';
          },
        },
      }),
      createdAt: columnHelper.accessor('createdAt', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text style={styles.cellText}>{date.fromNow()}</Text>;
          }
          return <Text style={styles.cellText}>-</Text>;
        },
        header: () => t('file.filter.columns.createdAt'),
        meta: {
          sortable: true,
          exportable: true,
          exportCell: (data: any) => {
            if (data.createdAt) {
              return moment(data.createdAt).format('YYYY-MM-DD HH:mm:ss');
            }
            return '';
          },
        },
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fileFilter]
  );

  useEffect(() => {
    if (!fileFilter) return;

    const columnList = [];
    if (showCheckboxColumn) {
      columnList.push(allColumns.selected);
    }

    columnList.push(allColumns.name);

    // For Producer workspaces, only show assets count, not products or addons
    if (isProducerWorkspace) {
      // For Producer workspaces, only include specific columns and exclude products/addons
      const producerAllowedColumns = [
        'assetsCount',
        'updatedAt',
        'categories',
        'createdAt',
      ];

      producerAllowedColumns.forEach((key) => {
        if (allColumns[key]) {
          columnList.push(allColumns[key]);
        }
      });
    } else {
      // For OEM workspaces, show all columns in specific order
      const oemColumnOrder = [
        'productsCount',
        'assetsCount', 
        'addonsCount',
        'updatedAt',
        'categories',
        'createdAt',
      ];
      
      oemColumnOrder.forEach((key) => {
        if (fileFilter.columns?.includes(key) && allColumns[key]) {
          columnList.push(allColumns[key]);
        }
      });
    }

    setColumns(columnList);
  }, [fileFilter, allColumns, showCheckboxColumn, isProducerWorkspace]);

  return columns;
};

const styles = StyleSheet.create({
  cell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingVertical: 4,
    ...Platform.select({
      web: {
        height: 50,
        paddingRight: 16,
      },
      default: {
        height: 68,
        paddingRight: 12,
      },
    }),
  },
  filesContainer: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
  nameCell: {
    flex: 1,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
  },
  emailText: {
    overflow: 'hidden',
  },
  routine: { flexDirection: 'row', alignItems: 'center' },
  routineIcon: {
    marginRight: 6,
    color: Colors.blue,
  },
  clickableFileName: {
    cursor: 'pointer',
  },
  countContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  countIcon: {
    marginRight: 6,
  },
});
